/**
 * LERNZENTRUM — das Funktionsregister (v1: 20 Kernkapitel)
 *
 * Eine Quelle der Wahrheit (Plan §1.1). Anleitungstext, Screenshots, Tour,
 * Sprechertext, Video, Untertitel, FAQ, Chat-Kontext und Lernpfad entstehen
 * ALLE hieraus. Jede zweite Quelle driftet von der Funktion weg; deshalb gibt
 * es keine.
 *
 * Kapitelkatalog und IDs: `docs/lernzentrum-tutorial-plan.md` §5. v1 baut die
 * 20 Kernkapitel (Entscheidung 4 in `docs/lernzentrum-status.md`), der Rest
 * des Katalogs folgt in Paket F. Eine ID wird NIE umbenannt — an ihr hängen
 * Fortschritt (`tutorial_fortschritt`), Medien im Bucket und der Chat-Kontext.
 *
 * ─── Anker ──────────────────────────────────────────────────────────────────
 * Konvention `data-tutorial="<bereich>.<element>"`, z. B. `begehung.neuer-bericht`
 * oder `sidebar.projekte`. Der Anker benennt das ELEMENT, nicht den Schritt —
 * so überlebt er das Umsortieren eines Kapitels und kann in mehreren Kapiteln
 * auftauchen (`begehung.zaehler` steht in `begehung.neu` und in
 * `begehung.stolperfallen`).
 *
 * Die Attribute selbst setzt Auftrag A2b in die Komponenten. Bis dahin meldet
 * `npm run tutorial:check` sie als fehlend; mit `--ohne-anker` bleibt der Lauf
 * grün. Welches Element welchen Anker bekommt, steht im Auftrag A2b — die
 * Zieltabelle wurde beim Schreiben dieses Registers Zeile für Zeile aus den
 * Komponenten gelesen, nicht geraten.
 *
 * ─── Warum manche Schritte `zeigen` sind ────────────────────────────────────
 * Die Tour im Browser führt nichts aus (Plan §1.3). `klick` und `tippen` sagen
 * dem Pipeline-Roboter, was er im Demo-Projekt tun soll; dem Nutzer wird
 * beides nur erklärt. `zeigen` ist für Elemente, die man NICHT bedient —
 * Riegel, Warnbanner, Zähler, Statusanzeigen.
 */

import type { AppView } from '../types';
import type { TutorialKapitel, TutorialKapitelId, TutorialRolle } from './types';

export const TUTORIAL_REGISTER: TutorialKapitel[] = [
    // ─── Einstieg ────────────────────────────────────────────────────────────
    {
        id: 'einstieg.oberflaeche',
        bereich: 'einstieg',
        titel: 'Oberfläche und Seitenleiste',
        view: 'dashboard',
        rollen: ['K', 'I', 'V'],
        dauerMin: 3,
        schritte: [
            { anker: 'sidebar.leiste', aktion: 'zeigen', stichwort: 'Die Seitenleiste ist die einzige Navigation — die App hat keine Adresszeile, der Zurück-Knopf des Browsers hilft hier nicht.' },
            { anker: 'sidebar.dashboard', aktion: 'klick', stichwort: 'Das Dashboard ist der Startpunkt jedes Arbeitstages.' },
            { anker: 'sidebar.projekte', aktion: 'klick', stichwort: 'Über „Projekte" kommst du in jedes Projekt; die Zahl am Eintrag zeigt eingehende Übergaben.' },
            { anker: 'sidebar.aufgaben', aktion: 'zeigen', stichwort: 'Alles, was fällig oder liegengeblieben ist, sammelt sich unter „Aufgaben".' },
            { anker: 'sidebar.einklappen', aktion: 'klick', stichwort: 'Eingeklappt bleiben nur die Symbole — nützlich auf kleinen Bildschirmen.' },
            { anker: 'sidebar.profil', aktion: 'klick', stichwort: 'Ein Klick auf deinen Namen öffnet Profil, Hilfe, App-Download und Abmelden — unter dem Namen steht der Speicherstand des offenen Projekts.' },
        ],
        praxis: {
            kontext: 'Erster Tag mit OS-IA, noch kein Projekt offen.',
            ziel: 'Sich in der Oberfläche zurechtfinden und ein Projekt öffnen können.',
        },
    },
    {
        id: 'einstieg.dashboard',
        bereich: 'einstieg',
        titel: 'Dashboard lesen: fällige Begehungen, Aufgaben, Erwähnungen',
        view: 'dashboard',
        rollen: ['K'],
        dauerMin: 4,
        voraussetzungen: ['einstieg.oberflaeche'],
        schritte: [
            { anker: 'dashboard.begruessung', aktion: 'zeigen', stichwort: 'Oben steht, wer angemeldet ist — wichtig auf geteilten Rechnern.' },
            { anker: 'dashboard.kennzahlen', aktion: 'zeigen', stichwort: 'Die Chips zählen deine Projekte nach Zustand: aktiv, Planung, Vertretung, ruhend.' },
            { anker: 'dashboard.faellige-begehungen', aktion: 'zeigen', stichwort: 'Links stehen die Projekte, deren Begehungsrhythmus abgelaufen ist — rot heißt überfällig.' },
            { anker: 'dashboard.faellige-zeile', aktion: 'klick', stichwort: 'Ein Klick auf die Zeile öffnet das Projekt direkt in der Zentrale.' },
            { anker: 'dashboard.aufgaben-kachel', aktion: 'zeigen', stichwort: 'Die Kachel rechts springt in die Aufgabenliste und zeigt vorab, wie viel dringend ist.' },
            { anker: 'dashboard.erwaehnungen', aktion: 'zeigen', stichwort: 'Erwähnungen sind Nachrichten aus Projekten, in denen dich jemand genannt hat.' },
        ],
        praxis: {
            kontext: 'Montagmorgen, Projekt 26099001 „Neubau Kita Beispielstadt" ist seit drei Tagen überfällig.',
            ziel: 'Die Woche planen und die überfällige Begehung als erstes anfassen.',
        },
    },
    {
        id: 'einstieg.aufgaben',
        bereich: 'einstieg',
        titel: 'Aufgabenliste abarbeiten',
        view: 'tasks',
        rollen: ['K', 'I'],
        dauerMin: 4,
        voraussetzungen: ['einstieg.dashboard'],
        schritte: [
            { anker: 'aufgaben.reiter-aufgaben', aktion: 'klick', stichwort: 'Zwei Reiter: Aufgaben und Erwähnungen — die roten Zahlen zählen das Dringende.' },
            { anker: 'aufgaben.filter-dringend', aktion: 'klick', stichwort: 'Der Filter engt auf „Dringend", „Bald fällig" oder „Offen" ein.' },
            { anker: 'aufgaben.projektgruppe', aktion: 'zeigen', stichwort: 'Aufgaben sind nach Projekt gruppiert, nicht nach Datum — so arbeitest du ein Projekt in einem Rutsch ab.' },
            { anker: 'aufgaben.zeile', aktion: 'zeigen', stichwort: 'Eine Zeile ist entweder ein fehlendes Dokument — Vorankündigung, Unterlage für spätere Arbeiten, Planungsphase — oder eine überfällige Begehung.' },
            { anker: 'aufgaben.projekt-oeffnen', aktion: 'klick', stichwort: 'Erledigt wird die Aufgabe im Projekt — von hier springst du direkt hinein.' },
        ],
        praxis: {
            kontext: 'Ein Projekt startet in zehn Tagen, die Vorankündigung ist noch nicht raus.',
            ziel: 'Die offenen Dokumente abarbeiten, bis die Liste leer ist.',
        },
        stolperfallen: [
            'Eine Aufgabe wird nie von Hand angelegt — sie entsteht aus dem Projektstand und verschwindet, sobald die Ursache weg ist.',
            'SiGe-Plan, A+S-Plan, Einweisung und Baustellenordnung stehen hier absichtlich nicht — ihr Stand steht auf der Kachel im Projekt-Hub.',
        ],
    },
    {
        id: 'einstieg.projektsperre',
        bereich: 'einstieg',
        titel: 'Projektsperre, Übernahme und Nur-Ansicht',
        view: 'project-hub',
        rollen: ['K', 'I'],
        dauerMin: 3,
        voraussetzungen: ['einstieg.oberflaeche'],
        schritte: [
            { anker: 'sperre.hinweis', aktion: 'zeigen', stichwort: 'Ein Schloss an der Projektzeile heißt: jemand anderes hat das Projekt offen.' },
            { anker: 'sperre.banner', aktion: 'zeigen', stichwort: 'Öffnest du es trotzdem, steht oben das gelbe Band — du bist in der Nur-Ansicht und nichts wird gespeichert.' },
            { anker: 'sperre.uebernehmen', aktion: 'klick', stichwort: 'Übernehmen holt die Sperre zu dir. Der Kollege verliert dabei seine ungespeicherten Änderungen.' },
            { anker: 'sperre.speicherstand', aktion: 'zeigen', stichwort: 'Unten in der Seitenleiste steht immer, ob gespeichert wird — „Nur-Ansicht" heißt: nein.' },
        ],
        praxis: {
            kontext: 'Ein Kollege hat 26099001 heute Vormittag offen gelassen.',
            ziel: 'Erkennen, warum nichts speicherbar ist, und die Sperre bewusst übernehmen.',
        },
        stolperfallen: [
            'Die Sperre löst sich nach längerer Untätigkeit von selbst — das Band mit dem Countdown ist die Vorwarnung, nicht der Fehler.',
        ],
    },

    // ─── Projekte ────────────────────────────────────────────────────────────
    {
        id: 'projekt.anlegen',
        bereich: 'projekt',
        titel: 'Projekt anlegen: Typ, Bauherr, LV, Intervall',
        view: 'projects',
        rollen: ['K', 'I'],
        dauerMin: 6,
        voraussetzungen: ['einstieg.oberflaeche'],
        schritte: [
            { anker: 'projekt.name', aktion: 'tippen', wert: 'Neubau Kita Beispielstadt', stichwort: 'Der Projektname steht später auf jedem Bericht und jeder Rechnung.' },
            { anker: 'projekt.typ', aktion: 'klick', stichwort: 'Der Typ entscheidet, welche Module das Projekt überhaupt bekommt — SiGeKo, SiFa, HSE oder Digital.' },
            { anker: 'projekt.nummer', aktion: 'zeigen', stichwort: 'Die Projektnummer wird aus dem Nummernkreis erzeugt; nur mit „Manuell" trägst du eine eigene ein.' },
            { anker: 'projekt.standort', aktion: 'tippen', wert: 'Musterstraße 12, 12345 Beispielstadt', stichwort: 'Die Adresse wird beim Verlassen des Feldes verortet — davon leben Projektkarte und Notrufplan.' },
            { anker: 'projekt.intervall', aktion: 'klick', stichwort: 'Das Begehungsintervall steuert, wann das Projekt im Dashboard fällig wird.' },
            { anker: 'projekt.bauherr', aktion: 'tippen', wert: 'Stadtwerke Beispielstadt', stichwort: 'Bauherr und Auftraggeber sind zwei Felder — sie sind oft, aber nicht immer dieselbe Firma.' },
            { anker: 'projekt.lv-position', aktion: 'klick', stichwort: 'Im zweiten Schritt kommt das Leistungsverzeichnis dazu; ohne LV lässt sich später nichts abrechnen.' },
            { anker: 'projekt.wizard-weiter', aktion: 'klick', stichwort: 'Derselbe Knopf führt erst zum LV und legt dann das Projekt an.' },
        ],
        praxis: {
            kontext: 'Die Stadtwerke Beispielstadt haben den SiGeKo für den Kita-Neubau beauftragt.',
            ziel: 'Projekt 26099001 mit LV und 14-tägigem Rhythmus anlegen.',
        },
        stolperfallen: [
            'Das Intervall ist keine Kosmetik: Es setzt den Anker, an dem der Begehungszähler hängt. Ein späterer Nachtrag kann ihn verstellen.',
        ],
    },
    {
        id: 'projekt.hub',
        bereich: 'projekt',
        titel: 'Projekt-Zentrale: Kacheln, Kennzahlen, Stammdaten',
        view: 'project-hub',
        rollen: ['K', 'I'],
        dauerMin: 4,
        voraussetzungen: ['projekt.anlegen'],
        schritte: [
            { anker: 'hub.kopf', aktion: 'zeigen', stichwort: 'Die Projekt-Zentrale ist der Verteiler — von hier geht es in jedes Modul und wieder zurück.' },
            { anker: 'hub.kennzahlen', aktion: 'zeigen', stichwort: 'Drei Karten: Zeitplan und Intervall, Leistung und Budget, Mitteilungszentrale.' },
            { anker: 'hub.modulraster', aktion: 'zeigen', stichwort: 'Die Kacheln stehen in der Reihenfolge, in der ein Projekt normalerweise läuft — die Nummern sind der Fahrplan.' },
            { anker: 'hub.kachel-sigeplan', aktion: 'zeigen', stichwort: 'Die SiGe-Plan-Kachel enthält auch Gewerke-Überschneidung und Handbuch; sie haben keine eigene Kachel mehr.' },
            { anker: 'hub.kachel-begehung', aktion: 'klick', stichwort: 'Die Begehungskachel zeigt den Verbrauch als „genutzt / beauftragt" aus dem LV.' },
            { anker: 'hub.stammdaten', aktion: 'klick', stichwort: 'Unter den Stammdaten änderst du Name, Adresse, Typ und Verantwortlichkeiten — direkt im Feld, ohne Bearbeiten-Knopf.' },
        ],
        praxis: {
            kontext: 'Vor der dritten Begehung auf 26099001.',
            ziel: 'Den Stand des Projekts in einem Blick erfassen und ins richtige Modul abbiegen.',
        },
    },
    {
        id: 'projekt.beteiligte',
        bereich: 'projekt',
        titel: 'Beteiligte, Verteiler und Vertreter',
        view: 'project-hub',
        rollen: ['K', 'I'],
        dauerMin: 5,
        voraussetzungen: ['projekt.hub'],
        schritte: [
            { anker: 'beteiligte.aufklappen', aktion: 'klick', stichwort: '„Projektbeteiligte & Verteiler" klappt in der Zentrale auf.' },
            { anker: 'beteiligte.neu', aktion: 'klick', stichwort: 'Jeder Beteiligte wird einmal angelegt und dann überall weiterverwendet.' },
            { anker: 'beteiligte.name', aktion: 'tippen', wert: 'Petra Mustermann', stichwort: 'Name und Firma trennen — die Firma erscheint im Bericht als Verantwortliche.' },
            { anker: 'beteiligte.rolle', aktion: 'klick', stichwort: 'Die Rolle steuert, wo der Kontakt später vorgeschlagen wird.' },
            { anker: 'beteiligte.speichern', aktion: 'klick', stichwort: 'Gespeichert wird der Kontakt am Projekt, nicht global.' },
            { anker: 'beteiligte.verteiler', aktion: 'klick', stichwort: '„Verteiler" entscheidet, wer den Bericht per E-Mail bekommt — „Teilnehmer" ist etwas anderes.' },
            { anker: 'beteiligte.vertreter', aktion: 'zeigen', stichwort: 'Vertreter stehen nicht hier, sondern in den Stammdaten unter „Verantwortlichkeiten".' },
        ],
        praxis: {
            kontext: 'Der Bauleiter der Musterbau GmbH wechselt; der neue soll den Bericht bekommen.',
            ziel: 'Kontakt anlegen, in den Verteiler nehmen, den alten herausnehmen.',
        },
        stolperfallen: [
            'Verteiler und Teilnehmer sind zwei Schalter an derselben Karte. Wer den falschen setzt, hat entweder eine leere Anwesenheitsliste oder einen Bericht, der nie ankommt.',
        ],
    },
    {
        id: 'projekt.leistungen',
        bereich: 'projekt',
        titel: 'Leistungen buchen, Pauschalmonat, Nachtrag',
        view: 'project-hub',
        rollen: ['K', 'I'],
        dauerMin: 5,
        voraussetzungen: ['projekt.hub'],
        schritte: [
            { anker: 'leistung.karte', aktion: 'zeigen', stichwort: '„Leistung & Budget" zeigt je LV-Position, wie viel beauftragt und wie viel verbraucht ist.' },
            { anker: 'leistung.position', aktion: 'klick', stichwort: 'Ein Klick auf die Position öffnet die Buchung.' },
            { anker: 'leistung.datum', aktion: 'tippen', wert: '12.09.2026', stichwort: 'Das Datum ist das Leistungsdatum, nicht der Tag der Eingabe — daran hängt der Abrechnungsmonat.' },
            { anker: 'leistung.menge', aktion: 'tippen', wert: '1', stichwort: 'Die Menge in der Einheit der Position: Stück, Stunden oder Pauschale.' },
            { anker: 'leistung.bericht', aktion: 'tippen', wert: 'Dritte Baustellenbegehung, Schwerpunkt Treppenhaus', stichwort: 'Der Tätigkeitsbericht landet im Leistungsnachweis, den der Kunde zur Rechnung bekommt.' },
            { anker: 'leistung.speichern', aktion: 'klick', stichwort: 'Erst das Speichern schreibt die Buchung ins Journal.' },
            { anker: 'leistung.pauschalmonat', aktion: 'zeigen', stichwort: 'Bei Monatspauschalen schlägt die App den fälligen Monat vor, statt Einzelbuchungen zu verlangen.' },
            { anker: 'leistung.nachtrag', aktion: 'zeigen', stichwort: 'Fehlt eine Position ganz, gehört sie in den Nachtrag — nicht als Übermenge auf eine fremde Position.' },
        ],
        praxis: {
            kontext: 'Ein Zusatztermin auf 26099001, der nicht im Rhythmus lag.',
            ziel: 'Den Termin auf die richtige LV-Position buchen.',
        },
        stolperfallen: [
            'Ein Nachtrag kann den Begehungsrhythmus verstellen; nach jedem Nachtrag den Zeitplan in der Zentrale prüfen.',
            'Eine Buchung von Hand ist kein Beleg. Sie ersetzt keine Rechnung und taucht in der Belegliste nicht auf.',
        ],
    },
    {
        id: 'projekt.dokumente',
        bereich: 'projekt',
        titel: 'Dokumente ablegen, bearbeiten, wiederfinden',
        view: 'module-dokumente',
        rollen: ['K', 'I'],
        dauerMin: 4,
        voraussetzungen: ['projekt.hub'],
        schritte: [
            { anker: 'dokumente.ablegen', aktion: 'klick', stichwort: '„Ablegen" öffnet den Upload; mehrere Dateien auf einmal sind erlaubt.' },
            { anker: 'dokumente.ablagefeld', aktion: 'klick', stichwort: 'Dateien lassen sich auch einfach in das Feld ziehen.' },
            { anker: 'dokumente.kategorie', aktion: 'klick', stichwort: 'Die Kategorie bestimmt den Reiter, unter dem das Dokument später steht.' },
            { anker: 'dokumente.hochladen', aktion: 'klick', stichwort: 'Erst nach dem Hochladen ist die Datei im Projekt und für die Kollegen sichtbar.' },
            { anker: 'dokumente.zeile', aktion: 'zeigen', stichwort: 'Jede Zeile zeigt Titel, Ordner, Stand und ein etwaiges Ablaufdatum.' },
            { anker: 'dokumente.bearbeiten', aktion: 'klick', stichwort: 'Über den Stift änderst du Angaben nachträglich, ohne die Datei neu hochzuladen.' },
        ],
        praxis: {
            kontext: 'Die Baugenehmigung für die Kita ist per Mail gekommen.',
            ziel: 'Sie so ablegen, dass sie ein Kollege ohne Nachfrage findet.',
        },
    },

    // ─── Begehung ────────────────────────────────────────────────────────────
    {
        id: 'begehung.neu',
        bereich: 'begehung',
        titel: 'Neuen Bericht beginnen, Folgebericht, laufende Nummer',
        view: 'module-sigeko-inspection',
        rollen: ['K'],
        dauerMin: 5,
        voraussetzungen: ['projekt.hub'],
        schritte: [
            { anker: 'begehung.zaehler', aktion: 'zeigen', stichwort: 'Der Zähler sagt, die wievielte Begehung das wird — er zählt hoch, sobald der Bericht angelegt ist.' },
            { anker: 'begehung.folgebericht', aktion: 'zeigen', stichwort: 'Ein Folgebericht übernimmt die offenen Mängel des Vorgängers.' },
            { anker: 'begehung.neuer-bericht', aktion: 'klick', stichwort: 'Der erste Klick öffnet nur die Bestätigung — angelegt ist noch nichts.' },
            { anker: 'begehung.datum', aktion: 'tippen', wert: '12.09.2026', stichwort: 'Das Begehungsdatum, nicht das Datum der Eingabe.' },
            { anker: 'begehung.uhrzeit', aktion: 'tippen', wert: '09:30', stichwort: 'Die Uhrzeit steht im Kopf des Berichts und in der Anwesenheitsliste.' },
            { anker: 'begehung.anlegen-bestaetigen', aktion: 'klick', stichwort: 'Erst dieser Knopf legt den Bericht an und setzt die laufende Nummer.' },
            { anker: 'begehung.nummer', aktion: 'zeigen', stichwort: 'Oben steht ab jetzt, an welchem Bericht du arbeitest.' },
        ],
        praxis: {
            kontext: 'Dritte Begehung auf der Kita-Baustelle, zwei Mängel aus der zweiten sind noch offen.',
            ziel: 'Bericht #3 als Folgebericht anlegen.',
        },
        stolperfallen: [
            'Die laufende Nummer wird beim Anlegen vergeben, nicht beim Versenden. Ein angelegter und wieder verworfener Bericht hinterlässt eine Lücke.',
            'Ein Folgebericht, der leer versendet wird, bucht trotzdem eine Begehung.',
        ],
    },
    {
        id: 'begehung.mangel-foto',
        bereich: 'begehung',
        titel: 'Mangel mit Foto und KI-Analyse erfassen',
        view: 'module-sigeko-inspection',
        rollen: ['K'],
        dauerMin: 6,
        voraussetzungen: ['begehung.neu'],
        schritte: [
            { anker: 'mangel.neuer-eintrag', aktion: 'klick', stichwort: 'Jede Feststellung ist ein eigener Eintrag — nie mehrere Mängel in einen Text.' },
            { anker: 'mangel.titel', aktion: 'tippen', wert: 'Fehlender Seitenschutz im Treppenhaus', stichwort: 'Der Titel ist die Zeile, die der Bauleiter zuerst liest.' },
            { anker: 'mangel.foto-feld', aktion: 'klick', stichwort: 'Das Foto ist der Beweis; ohne Bild ist ein Mangel auf der Baustelle schwer zu bestreiten.' },
            { anker: 'mangel.ki-analyse', aktion: 'klick', stichwort: 'Die KI liest das Bild und schlägt Titel, Beschreibung und Vorschrift vor — sie ersetzt deine Prüfung nicht.' },
            { anker: 'mangel.beschreibung', aktion: 'zeigen', stichwort: 'In „Beobachtung & Maßnahmen" steht, was zu tun ist, nicht nur was falsch ist.' },
            { anker: 'mangel.regelwerk', aktion: 'zeigen', stichwort: 'Die Vorschrift macht aus einer Meinung eine Forderung.' },
            { anker: 'mangel.bereich', aktion: 'tippen', wert: 'Treppenhaus, 2. OG', stichwort: 'Ohne Ortsangabe sucht der Polier auf der ganzen Baustelle — das Feld ist Pflicht.' },
            { anker: 'mangel.status-mangel', aktion: 'klick', stichwort: 'Mangel, Hinweis, Positiv oder Information: nur „Mangel" erzeugt eine Frist.' },
        ],
        praxis: {
            kontext: 'Im Treppenhaus des zweiten Obergeschosses fehlt der Seitenschutz.',
            ziel: 'Den Mangel mit Foto, Ort, Vorschrift und Verantwortlichem erfassen.',
        },
    },
    {
        id: 'begehung.teilnehmer',
        bereich: 'begehung',
        titel: 'Teilnehmer, Gäste und Zurücksetzen',
        view: 'module-sigeko-inspection',
        rollen: ['K'],
        dauerMin: 3,
        voraussetzungen: ['begehung.neu'],
        schritte: [
            { anker: 'teilnehmer.oeffnen', aktion: 'klick', stichwort: '„Teilnehmer der Begehung" öffnet die Anwesenheitsliste für genau diesen Bericht.' },
            { anker: 'teilnehmer.suche', aktion: 'tippen', wert: 'Polier', stichwort: 'Gesucht wird über Name, Firma und Rolle zugleich.' },
            { anker: 'teilnehmer.zeile', aktion: 'klick', stichwort: 'Ein Klick setzt den Haken; die Auswahl gilt nur für diesen Bericht.' },
            { anker: 'teilnehmer.keine', aktion: 'zeigen', stichwort: '„Keine" leert die Liste vollständig — auch die Gäste.' },
            { anker: 'teilnehmer.gast', aktion: 'klick', stichwort: 'Wer nicht im Verteiler steht, kommt als Gast dazu, ohne Projektkontakt zu werden.' },
            { anker: 'teilnehmer.gast-name', aktion: 'tippen', wert: 'Jens Beispiel', stichwort: 'Name und Firma reichen; eine E-Mail braucht ein Gast nicht.' },
            { anker: 'teilnehmer.fertig', aktion: 'klick', stichwort: 'Die Zahl im Knopf ist, was später in der Anwesenheitsliste im PDF steht.' },
        ],
        praxis: {
            kontext: 'Ein neuer Polier ist dabei, der noch in keinem Verteiler steht.',
            ziel: 'Die Anwesenheit korrekt festhalten, ohne den Projektverteiler zu verändern.',
        },
        stolperfallen: [
            'Die Teilnehmerliste setzt sich beim neuen Bericht zurück. Wer sie nicht neu setzt, versendet eine leere Anwesenheit.',
        ],
    },
    {
        id: 'begehung.erledigt',
        bereich: 'begehung',
        titel: 'Fristen, Sofortmaßnahme und Freimeldung',
        view: 'module-sigeko-inspection',
        rollen: ['K'],
        dauerMin: 4,
        voraussetzungen: ['begehung.mangel-foto'],
        schritte: [
            { anker: 'mangel.frist', aktion: 'tippen', wert: '19.09.2026', stichwort: 'Die Frist ist das Datum, bis zu dem der Mangel beseitigt sein muss.' },
            { anker: 'mangel.sofort', aktion: 'klick', stichwort: 'Eine Sofortmaßnahme hat keine Frist — sie duldet keinen Aufschub und ersetzt das Datumsfeld.' },
            { anker: 'mangel.erledigt', aktion: 'klick', stichwort: '„Erledigt" öffnet die Freimeldung, statt den Eintrag einfach verschwinden zu lassen.' },
            { anker: 'mangel.freimeldung-datum', aktion: 'tippen', wert: '18.09.2026', stichwort: 'Das Datum der Freimeldung ist der Tag der Behebung, nicht der Tag der Eingabe.' },
            { anker: 'mangel.freimeldung-kommentar', aktion: 'tippen', wert: 'Seitenschutz montiert, vor Ort geprüft', stichwort: 'Der Kommentar dokumentiert, wer wie geprüft hat.' },
            { anker: 'mangel.freimeldung-abschliessen', aktion: 'klick', stichwort: 'Der erledigte Mangel wandert in die Historie und verlässt die aktuelle Liste.' },
        ],
        praxis: {
            kontext: 'Der Seitenschutz aus Bericht #3 ist montiert.',
            ziel: 'Den Mangel mit Datum und Nachweis freimelden.',
        },
        stolperfallen: [
            'Erledigte Mängel stehen nicht mehr im Statusfilter der aktuellen Liste, sondern nur noch in der Historie.',
        ],
    },
    {
        id: 'begehung.pdf',
        bereich: 'begehung',
        titel: 'PDF-Einstellungen, Vorschau, Sprache',
        view: 'module-sigeko-inspection',
        rollen: ['K'],
        dauerMin: 4,
        voraussetzungen: ['begehung.mangel-foto'],
        schritte: [
            { anker: 'pdf.sprache-de', aktion: 'zeigen', stichwort: 'Die Sprache gilt für den ganzen Bericht, nicht nur für die Überschriften.' },
            { anker: 'pdf.sprache-en', aktion: 'klick', stichwort: 'Bei internationalen Bauherren erzeugst du denselben Bericht auf Englisch.' },
            { anker: 'pdf.optionen', aktion: 'klick', stichwort: 'Unter „Optionen" entscheidest du, welche Kapitel überhaupt gedruckt werden.' },
            { anker: 'pdf.historie', aktion: 'klick', stichwort: 'Die Historie der erledigten Mängel lässt sich auf einen Zeitraum eingrenzen — sonst wird das PDF mit der Zeit unlesbar dick.' },
            { anker: 'pdf.optionen-fertig', aktion: 'klick', stichwort: 'Die Einstellungen bleiben am Bericht hängen und gelten auch beim Versand.' },
            { anker: 'pdf.vorschau', aktion: 'klick', stichwort: 'Die Vorschau zeigt exakt das PDF, das der Kunde bekommt.' },
            { anker: 'pdf.herunterladen', aktion: 'klick', stichwort: 'Herunterladen ist für die eigene Ablage; für den Kunden nimmst du den Versand.' },
        ],
        praxis: {
            kontext: 'Der Bauherr der Kita will den Bericht zweisprachig.',
            ziel: 'Den Bericht mit Anwesenheitsliste und gekürzter Historie ausgeben.',
        },
    },
    {
        id: 'begehung.versand',
        bereich: 'begehung',
        titel: 'Versand per E-Mail, Sendeschutz, Sendeverlauf',
        view: 'module-sigeko-inspection',
        rollen: ['K'],
        dauerMin: 5,
        voraussetzungen: ['begehung.pdf'],
        schritte: [
            { anker: 'versand.abschliessen-senden', aktion: 'klick', stichwort: '„Abschließen & Senden" schließt den Bericht, bucht die Leistung und verschickt ihn — in einem Zug.' },
            { anker: 'versand.empfaenger', aktion: 'zeigen', stichwort: 'Die Empfängerliste kommt aus dem Verteiler; hier kannst du für diesen einen Versand abwählen.' },
            { anker: 'versand.betreff', aktion: 'zeigen', stichwort: 'Betreff und Text sind vorbelegt und dürfen angepasst werden.' },
            { anker: 'versand.absender', aktion: 'zeigen', stichwort: 'Unterzeichnet wird mit deinem Namen — der Empfänger sieht, wer gesendet hat.' },
            { anker: 'versand.senden', aktion: 'klick', stichwort: 'Nach dem Senden ist der Bericht im Archiv und nicht mehr bearbeitbar.' },
            { anker: 'versand.erneut-riegel', aktion: 'zeigen', stichwort: 'War der Bericht schon draußen, fragt die App nach — der zweite Versand ist eine bewusste Entscheidung.' },
            { anker: 'versand.sendeverlauf', aktion: 'zeigen', stichwort: 'Im Sendeverlauf steht je Zustellung, ob sie angekommen ist; von dort kannst du gezielt erneut senden.' },
        ],
        praxis: {
            kontext: 'Bericht #3 ist fertig, der Verteiler umfasst Bauherr, Bauleitung und zwei Firmen.',
            ziel: 'Den Bericht an den geprüften Verteiler senden.',
        },
        stolperfallen: [
            'Der Versand bucht die Leistung. Zweimal senden heißt ohne den Riegel zweimal buchen.',
            'Ein Zustellfehler steht nur im Sendeverlauf. Wer ihn nicht liest, hält einen Bericht für zugestellt, der nie ankam.',
        ],
    },
    {
        id: 'begehung.stolperfallen',
        bereich: 'begehung',
        titel: 'Stolperfallen: Doppelbuchung, leerer Folgebericht, Riegel',
        view: 'module-sigeko-inspection',
        rollen: ['K', 'I'],
        dauerMin: 5,
        voraussetzungen: ['begehung.versand'],
        schritte: [
            { anker: 'begehung.zaehler', aktion: 'zeigen', stichwort: 'Der Zähler und die Zahl der exportierten Berichte müssen zusammenpassen — klaffen sie auseinander, fehlt eine Buchung oder es gibt eine zu viel.' },
            { anker: 'begehung.folgebericht-gesperrt', aktion: 'zeigen', stichwort: 'Ein offener Entwurf sperrt den Folgebericht. Die Sperre zu lösen, ohne den Entwurf anzusehen, erzeugt den klassischen leeren Bericht.' },
            { anker: 'mangel.unvollstaendig', aktion: 'zeigen', stichwort: 'Das rote „Unvollständig" heißt: Pflichtfelder fehlen. Der Bericht geht trotzdem raus — die Lücke steht dann im PDF.' },
            { anker: 'billing.auswahl', aktion: 'zeigen', stichwort: 'Beim Abschließen wählst du, auf welche LV-Position gebucht wird. Bereits gebuchte Positionen sind gesperrt und nennen den Grund.' },
            { anker: 'versand.doppelsende-riegel', aktion: 'zeigen', stichwort: 'Zwei Riegel greifen hier: schon versendet und gleiche Nummer im Archiv. Beide sind Warnungen, keine Fehler.' },
            { anker: 'begehung.freigabe-riegel', aktion: 'zeigen', stichwort: 'Berichte von Freelancern gehen erst nach Freigabe raus — Vier-Augen-Prinzip.' },
        ],
        praxis: {
            kontext: 'Nachschau auf einem Projekt, bei dem der Zähler nicht zur Zahl der Berichte passt.',
            ziel: 'Die drei häufigsten Ursachen erkennen, bevor sie in der Abrechnung auffallen.',
        },
        stolperfallen: [
            'Doppelbuchung entsteht fast immer durch zweimaligen Versand desselben Berichts.',
            'Ein leerer Folgebericht bucht eine volle Begehung — vor dem Abschließen immer die Mängelliste ansehen.',
            'Nach einem Nachtrag kann der Begehungsrhythmus verstellt sein; der Zähler läuft dann gegen einen falschen Anker.',
        ],
    },

    // ─── SiGe-Plan ───────────────────────────────────────────────────────────
    {
        id: 'sigeplan.wizard',
        bereich: 'sigeplan',
        titel: 'SiGe-Plan in sechs Schritten anlegen',
        view: 'module-sige-plan',
        rollen: ['K'],
        dauerMin: 7,
        voraussetzungen: ['projekt.hub'],
        schritte: [
            { anker: 'sigeplan.wizard-schritte', aktion: 'zeigen', stichwort: 'Sechs Schritte: Plankopf, Grundlagen, Abschnitte, Gefährdungen, Blöcke, Prüfung.' },
            { anker: 'sigeplan.bauherr', aktion: 'tippen', wert: 'Stadtwerke Beispielstadt', stichwort: 'Der Plankopf übernimmt, was im Projekt schon steht — prüfen statt neu tippen.' },
            { anker: 'sigeplan.bauzeit-von', aktion: 'tippen', wert: '01.10.2026', stichwort: 'Die Bauzeit ist Pflicht; ohne sie lässt sich keine Zeitachse für die Gewerke bauen.' },
            { anker: 'sigeplan.weiter', aktion: 'klick', stichwort: 'Erst nach dem ersten Speichern existiert der Plan — die späteren Schritte laden ihn nach.' },
            { anker: 'sigeplan.beschreibung', aktion: 'tippen', wert: 'Neubau einer viergruppigen Kindertagesstätte in Massivbauweise', stichwort: 'Aus dieser Beschreibung leitet die KI die Arbeitsschritte ab — je konkreter, desto brauchbarer.' },
            { anker: 'sigeplan.arbeitsschritte-ki', aktion: 'klick', stichwort: 'Die Vorschläge kommen als Liste zum Abhaken, nichts wird ungefragt übernommen.' },
            { anker: 'sigeplan.gefaehrdungen-ki', aktion: 'klick', stichwort: 'Der KI-Lauf ergänzt je Arbeitsschritt Gefährdungen, Schutzmaßnahmen und Vorschriften.' },
            { anker: 'sigeplan.abschliessen', aktion: 'klick', stichwort: 'Der Wizard ist jederzeit verlassbar; Gespeichertes bleibt erhalten.' },
        ],
        praxis: {
            kontext: 'Für die Kita wird ein neuer SiGe-Plan gebraucht.',
            ziel: 'Vom leeren Projekt zu einem Plan mit Arbeitsschritten und Gefährdungen.',
        },
        stolperfallen: [
            'Wer mit dem Musterplan startet, überspringt die KI — der Muster-Inhalt muss danach von Hand auf das Bauvorhaben gezogen werden.',
        ],
    },
    {
        id: 'sigeplan.bloecke',
        bereich: 'sigeplan',
        titel: 'Blöcke, Arbeitsschritte und Gefährdungen pflegen',
        view: 'module-sige-plan',
        rollen: ['K'],
        dauerMin: 6,
        voraussetzungen: ['sigeplan.wizard'],
        schritte: [
            { anker: 'sigeplan.block-hinzufuegen', aktion: 'klick', stichwort: 'Blöcke sind die Bausteine des Plans: Piktogramme, Legende, Grundsätze, Notrufplan.' },
            { anker: 'sigeplan.block-bearbeiten', aktion: 'klick', stichwort: 'Jeder Block hat einen eigenen Editor; welcher, hängt vom Blocktyp ab.' },
            { anker: 'sigeplan.block-speichern', aktion: 'klick', stichwort: 'Gespeichert wird pro Block, nicht für den ganzen Plan.' },
            { anker: 'sigeplan.schritt-neu', aktion: 'klick', stichwort: 'Ein Arbeitsschritt ist eine Zeile im Plan: Was passiert wann, mit welchen Schutzmaßnahmen.' },
            { anker: 'sigeplan.schritt-bezeichnung', aktion: 'tippen', wert: 'Gerüstbau Nordfassade', stichwort: 'Die Bezeichnung wird aus dem Katalog vorgeschlagen — gleiche Begriffe machen Pläne vergleichbar.' },
            { anker: 'sigeplan.schritt-speichern', aktion: 'klick', stichwort: 'Zeitraum und Gewerk brauchst du für die Gewerke-Überschneidung.' },
            { anker: 'sigeplan.schritt-ki-vorschlag', aktion: 'klick', stichwort: 'Der KI-Vorschlag füllt Schutzmaßnahmen und Vorschriften zum markierten Schritt.' },
        ],
        praxis: {
            kontext: 'Das Gewerk Rohbau soll im Plan sauber abgebildet sein.',
            ziel: 'Arbeitsschritte mit Zeitraum, Gefährdungen und Maßnahmen hinterlegen.',
        },
    },
    {
        id: 'sigeplan.export',
        bereich: 'sigeplan',
        titel: 'Plan exportieren, Version ziehen, abrechnen',
        view: 'module-sige-plan',
        rollen: ['K', 'I'],
        dauerMin: 4,
        voraussetzungen: ['sigeplan.bloecke'],
        schritte: [
            { anker: 'sigeplan.version-neu', aktion: 'klick', stichwort: 'Vor jeder Fortschreibung eine Version ziehen — der alte Stand bleibt damit nachweisbar.' },
            { anker: 'sigeplan.vorschau-export', aktion: 'klick', stichwort: '„Vorschau & Export" baut das PDF aus dem aktuellen Stand.' },
            { anker: 'sigeplan.pdf-vorschau', aktion: 'zeigen', stichwort: 'Was hier steht, geht auch an den Bauherrn — Plankopf und Bauzeit noch einmal prüfen.' },
            { anker: 'sigeplan.pdf-herunterladen', aktion: 'klick', stichwort: 'Der Download startet zugleich die Abrechnung des Plans.' },
            { anker: 'billing.auswahl', aktion: 'zeigen', stichwort: 'Die Auswahl bestimmt, auf welche LV-Position der Plan gebucht wird; unvollständige Pläne fragen vorher nach.' },
        ],
        praxis: {
            kontext: 'Der Plan für die Kita ist fertig und soll abgerechnet werden.',
            ziel: 'PDF ausgeben und die Leistung genau einmal buchen.',
        },
        stolperfallen: [
            '„Trotzdem exportieren" umgeht die Abrechnung. Der Plan ist dann draußen, aber nie gebucht.',
        ],
    },

    // ─── Einweisung ──────────────────────────────────────────────────────────
    {
        id: 'einweisung.online',
        bereich: 'einweisung',
        titel: 'Einweisung online: Link, QR und Teilnahmen',
        view: 'module-sige-einweisung',
        rollen: ['K'],
        dauerMin: 5,
        voraussetzungen: ['sigeplan.export'],
        schritte: [
            { anker: 'einweisung.abschliessen', aktion: 'klick', stichwort: 'Erst eine abgeschlossene Einweisung lässt sich online stellen — vorher fehlt der Inhalt.' },
            { anker: 'einweisung.link-aktivieren', aktion: 'klick', stichwort: '„Link" schaltet die Online-Teilnahme frei; derselbe Knopf schaltet sie wieder ab.' },
            { anker: 'einweisung.link-kopieren', aktion: 'klick', stichwort: 'Der Link geht per Mail oder Messenger an den Nachzügler.' },
            { anker: 'einweisung.qr', aktion: 'klick', stichwort: 'Für die Baustelle ist der QR-Code der bequemere Weg.' },
            { anker: 'einweisung.qr-bild', aktion: 'zeigen', stichwort: 'Der Code lässt sich einzeln als Bild sichern.' },
            { anker: 'einweisung.aushang-pdf', aktion: 'klick', stichwort: 'Der Aushang im A4-Format ist zum Aufhängen am Bauzaun gedacht — Überschrift und Kurztext sind frei.' },
            { anker: 'einweisung.teilnahmen', aktion: 'klick', stichwort: 'Die Teilnahmen zeigen, wer sich über den Link eingetragen hat.' },
            { anker: 'einweisung.teilnahmen-tabelle', aktion: 'zeigen', stichwort: 'Zu jeder Online-Teilnahme gehört eine Unterschrift; die Liste landet im PDF der Einweisung.' },
        ],
        praxis: {
            kontext: 'Zwei Monteure kommen erst nach der Einweisung auf die Baustelle.',
            ziel: 'Beide per QR nachträglich einweisen und die Teilnahme nachweisen.',
        },
        stolperfallen: [
            'Solange die Einweisung nicht abgeschlossen ist, gibt es Link, QR und Teilnahmen schlicht nicht.',
        ],
    },
];

// ─── Helfer ──────────────────────────────────────────────────────────────────

/** Alle Kapitel, deren Tour in dieser View startet. */
export const kapitelFuerView = (view: AppView): TutorialKapitel[] =>
    TUTORIAL_REGISTER.filter((k) => k.view === view);

/**
 * Alle Kapitel, die jemand mit diesen Rollen sehen darf.
 *
 * Die Rollen werden ODER-verknüpft: Wer `['K','I']` mitbringt (ein `admin`),
 * sieht Koordinator- UND Innendienstkapitel. Reihenfolge bleibt Registerreihenfolge.
 */
export const kapitelFuerRolle = (rollen: TutorialRolle[]): TutorialKapitel[] =>
    TUTORIAL_REGISTER.filter((k) => k.rollen.some((r) => rollen.includes(r)));

export const kapitelNachId = (id: TutorialKapitelId): TutorialKapitel | undefined =>
    TUTORIAL_REGISTER.find((k) => k.id === id);

/**
 * Lernpfad: die sichtbaren Kapitel so sortiert, dass jede Voraussetzung vor
 * dem Kapitel steht, das sie braucht (topologisch, Kahn mit stabiler Ordnung).
 *
 * Zwei Feinheiten, die beide Absicht sind:
 *
 *  - Voraussetzungen auf Kapitel, die diese Rolle GAR NICHT sieht, werden
 *    fallengelassen statt das Kapitel zu blockieren. Sonst wäre
 *    `begehung.stolperfallen` für den Innendienst unerreichbar, nur weil
 *    `begehung.versand` ein Koordinatorkapitel ist.
 *  - Ein Zyklus (den `tutorial:check` verbietet) darf den Pfad nicht leeren:
 *    der Rest wird in Registerreihenfolge angehängt. Ein Lernpfad, der still
 *    Kapitel verschluckt, ist schlimmer als einer mit schiefer Reihenfolge.
 */
export const lernpfad = (rollen: TutorialRolle[]): TutorialKapitel[] => {
    const sichtbar = kapitelFuerRolle(rollen);
    const erreichbar = new Set(sichtbar.map((k) => k.id));
    const bedingungen = new Map(
        sichtbar.map((k) => [k.id, (k.voraussetzungen ?? []).filter((v) => v !== k.id && erreichbar.has(v))]),
    );

    const fertig = new Set<TutorialKapitelId>();
    const pfad: TutorialKapitel[] = [];

    let bewegt = true;
    while (bewegt) {
        bewegt = false;
        for (const k of sichtbar) {
            if (fertig.has(k.id)) continue;
            if (!(bedingungen.get(k.id) ?? []).every((v) => fertig.has(v))) continue;
            fertig.add(k.id);
            pfad.push(k);
            bewegt = true;
        }
    }

    for (const k of sichtbar) if (!fertig.has(k.id)) pfad.push(k);
    return pfad;
};
